/**
 * Vlerësimet e ekipit — ku eprori shkruan vlerësimin që punonjësi e sheh te
 * "Vlerësimi im".
 *
 * Deri tani tabela `worker_evaluations` kishte vetëm lexues: `/my-evaluation`
 * e shfaqte, por asnjë ekran s'e mbushte. Kjo faqe është shkruesi.
 *
 * Asnjë rregull nuk vendoset këtu. Kush mund të vlerësohet, cilat periudha dhe
 * cilat rezultate pranohen — të gjitha vijnë nga `GET /api/evaluations/team` dhe
 * `/options`. Serveri e rillogarit fushën për çdo kërkesë (`workers.supervisor_id`),
 * ndaj ajo që shfaqet këtu është ajo që lejohet vërtet, jo një kopje që mund të
 * dalë jashtë sinkronit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Typography, Stack, Chip, Button, IconButton, Avatar, Tooltip,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, AlertTitle,
  LinearProgress, Collapse, Skeleton, Divider, CircularProgress,
} from '@mui/material';
import SearchIcon            from '@mui/icons-material/Search';
import RefreshRoundedIcon    from '@mui/icons-material/RefreshRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import EditRoundedIcon       from '@mui/icons-material/EditRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import GroupsRoundedIcon     from '@mui/icons-material/GroupsRounded';
import PageHeader from '../components/common/PageHeader';
import usePermissions from '../hooks/usePermissions';
import PERMISSIONS from '../config/permissions/permissionsConfig';
import { authFetch } from '../services/apiServices';

const C = {
  ink: '#0F172A', ink700: '#334155', ink500: '#64748B', ink400: '#94A3B8',
  line: '#E5E7EB', hair: '#F1F5F9', surface: '#F8FAFC', white: '#FFFFFF',
  // Theksi është marka, jo indigo — i njëjti rregull si te raportet.
  accent: '#3E8FA3', good: '#15803D', warn: '#B45309', bad: '#B91C1C',
};

const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2];
})();

/** Ngjyra e rezultatit — shkalla lexohet me sy para se të lexohet me fjalë. */
const RESULT_TONE = {
  'Shkëlqyeshëm': { fg: '#15803D', bg: '#F0FDF4' },
  'Shumë mirë':   { fg: '#166534', bg: '#F0FDF4' },
  'Mirë':         { fg: '#1D4ED8', bg: '#EFF6FF' },
  'Mjaftueshëm':  { fg: '#B45309', bg: '#FFFBEB' },
  'Dobët':        { fg: '#B91C1C', bg: '#FEF2F2' },
};
const toneOf = (r) => RESULT_TONE[r] || { fg: C.ink500, bg: C.hair };

const STATUS_TONE = {
  'I përfunduar': { fg: '#15803D', bg: '#F0FDF4' },
  'Në pritje':    { fg: '#B45309', bg: '#FFFBEB' },
  'Draft':        { fg: C.ink500, bg: C.surface },
};

const initials = (n) => String(n || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toLocaleUpperCase('sq');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = { workerId: null, period: '', year: new Date().getFullYear(), evaluationDate: today(), resultText: '', status: 'I përfunduar', notes: '' };

export default function TeamEvaluationsPage() {
  const { can } = usePermissions();
  const canWrite = can(PERMISSIONS.PERFORMANCE_CREATE) || can(PERMISSIONS.PERFORMANCE_MANAGE);
  const canEdit  = can(PERMISSIONS.PERFORMANCE_EDIT)   || can(PERMISSIONS.PERFORMANCE_MANAGE);

  const [year, setYear]     = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [data, setData]     = useState({ workers: [], scope: 'team' });
  const [options, setOptions] = useState({ periods: [], results: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [expanded, setExpanded] = useState(null);        // workerId i hapur
  const [history, setHistory]   = useState({});          // workerId → rreshtat
  const [historyBusy, setHistoryBusy] = useState(null);

  const [form, setForm]   = useState(null);              // null = dialogu mbyllur
  /**
   * Shablloni që i takon punonjësit dhe pikët për kriter.
   *
   * Deri tani vlerësimi ishte një rezultat i vetëm nga pesë fjalë: «Mirë» pa
   * thënë për çfarë. Kur për punonjësin ekziston një shabllon, formulari
   * shfaq kriteret e tij dhe pika del nga to — e shpjegueshme rresht për
   * rresht. Pa shabllon, formulari mbetet saktësisht si më parë.
   */
  const [tpl, setTpl]         = useState(null);   // { criteria: [...] } ose null
  const [tplBusy, setTplBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await authFetch(`/api/evaluations/team?year=${year}`);
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) {
        setError('Nuk keni leje për të parë vlerësimet e ekipit.');
        setData({ workers: [], scope: 'team' });
        return;
      }
      if (!res.ok || json.success === false) {
        setError(json.error || `Nuk u morën të dhënat (HTTP ${res.status}).`);
        setData({ workers: [], scope: 'team' });
        return;
      }
      setData(json.data || { workers: [], scope: 'team' });
      setHistory({});
    } catch {
      setError('Nuk u lidh dot me serverin. Kontrollo lidhjen dhe provo përsëri.');
      setData({ workers: [], scope: 'team' });
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    authFetch('/api/evaluations/options')
      .then((r) => r.json())
      .then((j) => { if (j?.success) setOptions(j.data); })
      .catch(() => {});
  }, []);

  const openHistory = async (workerId) => {
    if (expanded === workerId) { setExpanded(null); return; }
    setExpanded(workerId);
    if (history[workerId]) return;
    setHistoryBusy(workerId);
    try {
      const r = await authFetch(`/api/evaluations?workerId=${workerId}`);
      const j = await r.json().catch(() => ({}));
      setHistory((p) => ({ ...p, [workerId]: j?.data || [] }));
    } catch {
      setHistory((p) => ({ ...p, [workerId]: [] }));
    } finally {
      setHistoryBusy(null);
    }
  };

  /**
   * Shablloni merret nga serveri për çdo hapje, jo një herë për faqen.
   *
   * Zgjedhja varet nga pozita dhe departamenti i atij punonjësi (pozita para
   * departamentit, departamenti para kompanisë), ndaj një kopje e vetme për
   * tërë ekipin do t'i jepte gjithkujt shabllonin e të parit.
   */
  const loadTemplate = useCallback(async (workerId, existing) => {
    setTpl(null);
    setTplBusy(true);
    try {
      const r = await authFetch(`/api/performance/template-for/${workerId}`);
      const j = await r.json().catch(() => ({}));
      const t = (r.ok && j?.success !== false) ? j.data : null;
      if (!t || !(t.criteria || []).length) return;

      // Te një vlerësim ekzistues, vlerat e ruajtura kanë përparësi ndaj
      // kritereve të shabllonit: kriteri mund të jetë riemërtuar që atëherë.
      const saved = existing?.scores || [];
      setTpl({
        ...t,
        criteria: t.criteria.map((c) => {
          const s = saved.find((x) => Number(x.criterion_id) === Number(c.id) || x.criterion_name === c.name);
          return {
            criterionId: c.id, name: c.name, weight: Number(c.weight),
            value: s && s.value !== null ? Number(s.value) : '',
            comment: s?.comment || '',
          };
        }),
      });
    } catch { /* pa shabllon, formulari mbetet si më parë */ }
    finally { setTplBusy(false); }
  }, []);

  const startNew = (w) => {
    setFormError('');
    setForm({ ...EMPTY, year, workerId: w.workerId, workerName: w.workerName });
    loadTemplate(w.workerId, null);
  };
  const startEdit = async (w, row) => {
    setFormError('');
    setForm({
      id: row.id, workerId: w.workerId, workerName: w.workerName,
      period: row.period, year: row.year, evaluationDate: (row.evaluationDate || '').slice(0, 10) || today(),
      resultText: row.resultText, status: row.status, notes: row.notes || '',
      cycleId: row.cycleId || '', lockedAt: row.lockedAt || null,
    });
    // Pikët e ruajtura lexohen nga vetë vlerësimi, jo nga lista: lista mban
    // vetëm rreshtin, jo kriteret.
    let existing = null;
    try {
      const r = await authFetch(`/api/evaluations/${row.id}`);
      const j = await r.json().catch(() => ({}));
      if (r.ok && j?.success !== false) existing = j.data;
    } catch { /* jo fatale */ }
    loadTemplate(w.workerId, existing);
  };

  const save = async () => {
    setSaving(true);
    setFormError('');
    try {
      const body = {
        workerId: form.workerId, period: form.period, year: Number(form.year),
        evaluationDate: form.evaluationDate, resultText: form.resultText,
        status: form.status, notes: form.notes,
        ...(form.cycleId ? { cycleId: Number(form.cycleId) } : {}),
        ...(tpl ? {
          templateId: tpl.id,
          scores: tpl.criteria.map((c) => ({
            criterionId: c.criterionId, criterionName: c.name,
            weight: c.weight, value: c.value === '' ? null : Number(c.value),
            comment: c.comment || null,
          })),
        } : {}),
      };
      const res = await authFetch(form.id ? `/api/evaluations/${form.id}` : '/api/evaluations', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        setFormError(json.error || `Nuk u ruajt (HTTP ${res.status}).`);
        return;
      }
      setForm(null);
      // Historiku i atij punonjësi rilexohet nga serveri, jo i ndrequr me dorë.
      setHistory((p) => { const n = { ...p }; delete n[body.workerId]; return n; });
      if (expanded === body.workerId) setExpanded(null);
      await load();
    } catch {
      setFormError('Nuk u lidh dot me serverin.');
    } finally {
      setSaving(false);
    }
  };

  const formValid = form && form.period && form.resultText && form.status && form.year;

  /**
   * Pika ndërsa plotësohet — vetëm për ta parë.
   *
   * E ruajtura vjen nga serveri me të njëjtën formulë; kjo është aty që
   * menaxheri të mos e ruajë verbërisht dhe ta zbulojë rezultatin më vonë.
   * Kriteri pa vlerë nuk numërohet: normalizohet te pesha e përdorur.
   */
  const livePreview = useMemo(() => {
    if (!tpl) return null;
    const rated = tpl.criteria.filter((c) => c.value !== '' && c.value !== null);
    if (!rated.length) return null;
    const used = rated.reduce((a, c) => a + Number(c.weight || 0), 0);
    if (!used) return null;
    const pts = rated.reduce((a, c) => a + ((Number(c.value) - 1) / 4) * Number(c.weight || 0), 0);
    return Math.round((pts / used) * 1000) / 10;
  }, [tpl]);

  const shown = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('sq');
    if (!q) return data.workers;
    return data.workers.filter((w) =>
      `${w.workerName} ${w.department} ${w.position}`.toLocaleLowerCase('sq').includes(q));
  }, [data.workers, search]);

  const pending = data.workers.filter((w) => !w.countThisYear).length;

  const thSx = {
    fontWeight: 700, fontSize: '0.65rem', py: 0.75, px: 1.25, whiteSpace: 'nowrap',
    color: C.ink500, bgcolor: C.surface, letterSpacing: '0.05em',
    textTransform: 'uppercase', borderBottom: `1px solid ${C.line}`,
  };
  const tdSx = { py: 0.75, px: 1.25, fontSize: '0.8rem', color: C.ink700, borderBottom: `1px solid ${C.hair}` };

  return (
    <Box>
      <PageHeader title="Vlerësimet e ekipit" />

      <Card elevation={0} sx={{ border: `1px solid ${C.line}`, borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>

          {/* ── Toolbar ── */}
          <Box sx={{
            px: 1.5, py: 1, display: 'flex', gap: 0.875, flexWrap: 'wrap',
            alignItems: 'center', borderBottom: `1px solid ${C.hair}`,
          }}>
            <FormControl size="small" sx={{ width: 100, '& .MuiOutlinedInput-root': { height: 34, borderRadius: '8px', fontSize: '0.8rem' } }}>
              <InputLabel>Viti</InputLabel>
              <Select label="Viti" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              size="small" placeholder="Kërko punonjës, departament, pozitë…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: '1 1 220px', minWidth: 180,
                '& .MuiOutlinedInput-root': { height: 34, borderRadius: '8px', fontSize: '0.8rem' },
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: C.ink400 }} /></InputAdornment> }}
            />

            <Chip
              size="small" icon={<GroupsRoundedIcon sx={{ fontSize: 15 }} />}
              label={data.scope === 'company' ? `Gjithë kompania · ${data.workers.length}` : `Ekipi im · ${data.workers.length}`}
              sx={{ height: 26, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#EEF2FF', color: C.accent, '& .MuiChip-icon': { color: C.accent } }}
            />
            {!loading && pending > 0 && (
              <Chip size="small" label={`${pending} pa vlerësim për ${year}`}
                sx={{ height: 26, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#FFFBEB', color: C.warn }} />
            )}

            <Tooltip title="Rifresko">
              <span style={{ marginLeft: 'auto' }}>
                <IconButton size="small" onClick={load} disabled={loading}
                  sx={{ border: `1px solid ${C.line}`, borderRadius: '8px', width: 34, height: 34 }}>
                  <RefreshRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {loading && <LinearProgress />}

          {!loading && error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.85rem' }}
                action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Provo përsëri</Button>}>
                <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Nuk u shfaq lista</AlertTitle>
                {error}
              </Alert>
            </Box>
          )}

          {!error && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 290px)', minHeight: 240 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 40 }} />
                    <TableCell sx={thSx}>Punonjësi</TableCell>
                    <TableCell sx={thSx}>Departamenti</TableCell>
                    <TableCell sx={thSx}>Vlerësimi i fundit</TableCell>
                    <TableCell sx={thSx}>Periudha</TableCell>
                    <TableCell sx={thSx}>Data</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: 'right' }}>Veprim</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [0, 1, 2, 3].map((i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={7} sx={tdSx}><Skeleton height={22} /></TableCell>
                      </TableRow>
                    ))
                  ) : shown.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.disabled">
                          {search
                            ? 'Asnjë punonjës nuk përputhet me kërkimin'
                            : data.scope === 'company'
                              ? 'Nuk ka punonjës aktivë'
                              : 'Asnjë punonjës nuk ju raporton juve. Lidhja vendoset te "Eprori" në dosjen e punonjësit.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : shown.map((w) => {
                    const last = w.last;
                    const tone = last ? toneOf(last.resultText) : null;
                    const open = expanded === w.workerId;
                    return (
                      <React.Fragment key={w.workerId}>
                        <TableRow hover sx={{ '& > td': { borderBottom: open ? 0 : `1px solid ${C.hair}` } }}>
                          <TableCell sx={{ ...tdSx, pr: 0 }}>
                            <IconButton size="small" onClick={() => openHistory(w.workerId)} aria-label="Historiku">
                              {open ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#EEF2FF', color: C.accent }}>
                                {initials(w.workerName)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.ink }}>{w.workerName}</Typography>
                                {w.position && <Typography noWrap sx={{ fontSize: '0.7rem', color: C.ink400 }}>{w.position}</Typography>}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={tdSx}>{w.department || '—'}</TableCell>
                          <TableCell sx={tdSx}>
                            {last ? (
                              <Chip size="small" label={last.resultText || '—'}
                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: tone.bg, color: tone.fg }} />
                            ) : (
                              <Typography sx={{ fontSize: '0.78rem', color: C.ink400 }}>Ende i pavlerësuar</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={tdSx}>{last ? `${last.period} ${last.year}` : '—'}</TableCell>
                          <TableCell sx={tdSx}>{last ? fmtDate(last.evaluationDate) : '—'}</TableCell>
                          <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                            {canWrite && (
                              <Button size="small" variant="outlined" startIcon={<RateReviewRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => startNew(w)}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', borderRadius: '8px', py: 0.25 }}>
                                Vlerëso
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Historiku — hapet me kërkesë, jo i ngarkuar për të gjithë. */}
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 3, py: 1.5, bgcolor: C.surface, borderBottom: `1px solid ${C.hair}` }}>
                                {historyBusy === w.workerId ? (
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={14} />
                                    <Typography sx={{ fontSize: '0.78rem', color: C.ink500 }}>Duke ngarkuar historikun…</Typography>
                                  </Stack>
                                ) : (history[w.workerId] || []).length === 0 ? (
                                  <Typography sx={{ fontSize: '0.78rem', color: C.ink400 }}>
                                    Asnjë vlerësim i regjistruar për këtë punonjës.
                                  </Typography>
                                ) : (
                                  <Stack divider={<Divider sx={{ borderColor: C.line }} />} spacing={0}>
                                    {(history[w.workerId] || []).map((row) => {
                                      const rt = toneOf(row.resultText);
                                      const st = STATUS_TONE[row.status] || STATUS_TONE.Draft;
                                      return (
                                        <Stack key={row.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.ink, minWidth: 130 }}>
                                            {row.period} {row.year}
                                          </Typography>
                                          <Chip size="small" label={row.resultText || '—'}
                                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: rt.bg, color: rt.fg }} />
                                          <Chip size="small" label={row.status}
                                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: st.bg, color: st.fg }} />
                                          <Typography sx={{ fontSize: '0.72rem', color: C.ink500 }}>{fmtDate(row.evaluationDate)}</Typography>
                                          {row.evaluatorName && (
                                            <Typography sx={{ fontSize: '0.72rem', color: C.ink400 }}>nga {row.evaluatorName}</Typography>
                                          )}
                                          {row.notes && (
                                            <Tooltip title={row.notes}>
                                              <Typography noWrap sx={{ fontSize: '0.72rem', color: C.ink500, flex: 1, minWidth: 0, fontStyle: 'italic' }}>
                                                {row.notes}
                                              </Typography>
                                            </Tooltip>
                                          )}
                                          {canEdit && (
                                            <Tooltip title="Ndrysho">
                                              <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => startEdit(w, row)}>
                                                <EditRoundedIcon sx={{ fontSize: 15 }} />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Formulari ── */}
      <Dialog open={!!form} onClose={saving ? undefined : () => setForm(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>
          {form?.id ? 'Ndrysho vlerësimin' : 'Vlerësim i ri'}
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.ink500 }}>{form?.workerName}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" fullWidth required>
                <InputLabel>Periudha</InputLabel>
                <Select label="Periudha" value={form?.period || ''}
                  onChange={(e) => setForm((p) => ({ ...p, period: e.target.value }))}>
                  {options.periods.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 110 }} required>
                <InputLabel>Viti</InputLabel>
                <Select label="Viti" value={form?.year || year}
                  onChange={(e) => setForm((p) => ({ ...p, year: Number(e.target.value) }))}>
                  {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" fullWidth required>
                <InputLabel>Rezultati</InputLabel>
                <Select label="Rezultati" value={form?.resultText || ''}
                  onChange={(e) => setForm((p) => ({ ...p, resultText: e.target.value }))}>
                  {options.results.map((r) => (
                    <MenuItem key={r} value={r}>
                      <Box component="span" sx={{
                        width: 8, height: 8, borderRadius: '50%', mr: 1,
                        display: 'inline-block', bgcolor: toneOf(r).fg,
                      }} />
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Statusi</InputLabel>
                <Select label="Statusi" value={form?.status || 'I përfunduar'}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                  {options.statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            <TextField
              size="small" type="date" label="Data e vlerësimit" InputLabelProps={{ shrink: true }}
              value={form?.evaluationDate || ''}
              onChange={(e) => setForm((p) => ({ ...p, evaluationDate: e.target.value }))}
            />

            {/* ── Kriteret e shabllonit ──────────────────────────────────────
                Shfaqen vetëm kur për këtë punonjës ekziston një shabllon. Pa
                të, formulari mbetet ai i vjetri: një rezultat nga pesë fjalë.
                Kështu vlerësimet e mëparshme vazhdojnë të shkruhen njësoj. */}
            {tplBusy && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: 12.5, color: C.ink500 }}>Po lexohet shablloni…</Typography>
              </Stack>
            )}
            {tpl && (
              <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 1.5, p: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{tpl.name}</Typography>
                  {/* Pika llogaritet këtu vetëm për ta parë ndërsa plotësohet;
                      ajo që ruhet vjen nga serveri, me të njëjtën formulë. */}
                  <Chip size="small" label={livePreview === null ? 'Pa pikë ende' : `${livePreview}%`}
                    sx={{ height: 22, fontWeight: 800, bgcolor: '#EEF6F8', color: C.accent }} />
                </Stack>
                <Stack spacing={1}>
                  {tpl.criteria.map((c, i) => (
                    <Stack key={c.criterionId || i} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{c.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: C.ink500 }}>Pesha {c.weight}%</Typography>
                      </Box>
                      <FormControl size="small" sx={{ width: 118 }}>
                        <InputLabel>Vlera</InputLabel>
                        <Select label="Vlera" value={c.value}
                          onChange={(e) => setTpl((p) => {
                            const next = [...p.criteria];
                            next[i] = { ...next[i], value: e.target.value };
                            return { ...p, criteria: next };
                          })}>
                          <MenuItem value="">—</MenuItem>
                          {[1, 2, 3, 4, 5].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  ))}
                </Stack>
                <Typography sx={{ fontSize: 11.5, color: C.ink500, mt: 1 }}>
                  Kriteri i palotësuar nuk numërohet si zero — pika llogaritet mbi atë që u vlerësua.
                </Typography>
              </Box>
            )}

            <TextField
              size="small" label="Shënime" multiline minRows={3}
              placeholder="Çfarë shkoi mirë, çfarë duhet përmirësuar, çfarë pritet deri në periudhën tjetër."
              value={form?.notes || ''}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              helperText="Punonjësi i lexon këto shënime te «Vlerësimi im»."
            />

            {formError && (
              <Alert severity="error" sx={{ fontSize: '0.8rem', borderRadius: 2 }}>{formError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setForm(null)} disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, color: C.ink500 }}>
            Anulo
          </Button>
          <Button variant="contained" disableElevation onClick={save} disabled={saving || !formValid}
            startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: C.accent, '&:hover': { bgcolor: '#3730A3' } }}>
            {form?.id ? 'Ruaj ndryshimet' : 'Ruaj vlerësimin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
